import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  commentsService,
  type Comment,
  type EntityType,
  type MentionCandidate,
} from '../services/comments'

interface CommentThreadProps {
  entityType: EntityType
  entityId: number
  className?: string
  /** Auto-refresh interval in ms. 0 disables. */
  pollMs?: number
}

const formatTime = (iso: string): string => {
  const d = new Date(iso)
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

export function CommentThread({
  entityType,
  entityId,
  className = '',
  pollMs = 0,
}: CommentThreadProps) {
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [mentionState, setMentionState] = useState<{
    open: boolean
    query: string
    candidates: MentionCandidate[]
  }>({ open: false, query: '', candidates: [] })
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  const load = useCallback(async () => {
    try {
      const data = await commentsService.list(entityType, entityId)
      setComments(data)
    } catch {
      // surface only on the first attempt
      if (loading) toast.error('Failed to load comments')
    } finally {
      setLoading(false)
    }
  }, [entityType, entityId, loading])

  useEffect(() => {
    load()
    if (!pollMs) return
    const t = setInterval(load, pollMs)
    return () => clearInterval(t)
  }, [load, pollMs])

  // Detect @-mention in the current draft to show the picker.
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    const pos = el.selectionStart ?? draft.length
    const before = draft.slice(0, pos)
    const match = /(?:^|\s)@([\w.-]{0,32})$/.exec(before)
    if (!match) {
      if (mentionState.open) setMentionState(s => ({ ...s, open: false }))
      return
    }
    const q = match[1]
    let cancelled = false
    commentsService.mentionCandidates(q).then(cands => {
      if (cancelled) return
      setMentionState({ open: true, query: q, candidates: cands })
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  const insertMention = (handle: string) => {
    const el = textareaRef.current
    if (!el) return
    const pos = el.selectionStart ?? draft.length
    const before = draft.slice(0, pos).replace(/@([\w.-]{0,32})$/, `@${handle} `)
    const after = draft.slice(pos)
    const next = before + after
    setDraft(next)
    setMentionState(s => ({ ...s, open: false }))
    requestAnimationFrame(() => {
      el.focus()
      const newPos = before.length
      el.setSelectionRange(newPos, newPos)
    })
  }

  const submit = async () => {
    if (!draft.trim()) return
    setSubmitting(true)
    try {
      const created = await commentsService.create(entityType, entityId, draft.trim())
      setComments(prev => [...prev, created])
      setDraft('')
    } catch {
      toast.error('Failed to post comment')
    } finally {
      setSubmitting(false)
    }
  }

  const remove = async (id: number) => {
    try {
      await commentsService.remove(id)
      setComments(prev => prev.filter(c => c.id !== id))
    } catch {
      toast.error('Failed to delete comment')
    }
  }

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      <div className="flex flex-col gap-2">
        {loading ? (
          <p className="text-sm text-[var(--color-text-secondary)]">Loading comments…</p>
        ) : comments.length === 0 ? (
          <p className="text-sm text-[var(--color-text-secondary)]">No comments yet.</p>
        ) : (
          comments.map(c => (
            <div
              key={c.id}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3"
            >
              <div className="flex items-center justify-between gap-2 text-xs text-[var(--color-text-secondary)]">
                <span className="font-medium text-[var(--color-text-primary)]">
                  {c.user?.full_name || c.user?.email || 'Someone'}
                </span>
                <span>{formatTime(c.created_at)}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">
                {c.content}
              </p>
              <div className="mt-2 flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => remove(c.id)}
                  className="min-h-[36px] text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-danger)]"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="relative">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Write a comment… use @ to mention"
          rows={3}
          className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-2 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
        />
        {mentionState.open && mentionState.candidates.length > 0 && (
          <ul className="absolute bottom-full left-0 mb-1 max-h-48 w-full max-w-xs overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg">
            {mentionState.candidates.map(c => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => insertMention(c.handle)}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-surface-hover)]"
                >
                  <span className="font-medium">@{c.handle}</span>
                  <span className="ml-2 text-xs text-[var(--color-text-secondary)]">
                    {c.full_name || c.email}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            disabled={!draft.trim() || submitting}
            onClick={submit}
            className="min-h-[44px] rounded-md bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? 'Posting…' : 'Comment'}
          </button>
        </div>
      </div>
    </div>
  )
}
